import React, { useState, useEffect } from 'react'
import { Box, Typography, Divider } from '@mui/material'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { EntityModal } from './EntityModal'
import { FormInput, FormSelect } from '../ui'
import { themeColors } from '../../app/layout/theme'

export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'

const PRIORITIES = [
  { value: 'LOW',    label: 'Low'    },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH',   label: 'High'   },
  { value: 'URGENT', label: 'Urgent' },
]

const LEAD_STATUSES = [
  { value: 'PENDING',     label: 'Pending'     },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'DONE',        label: 'Done'        },
  { value: 'CANCELLED',   label: 'Cancelled'   },
]

const DEAL_STATUSES = [
  { value: 'OPEN',        label: 'Open'        },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'DONE',        label: 'Done'        },
]

// Returns the current local datetime as "YYYY-MM-DDTHH:MM" (no seconds),
// suitable as the `min` value for a datetime-local input.
const getNowLocal = () => {
  const now = new Date()
  const tzOffset = now.getTimezoneOffset() * 60000
  return new Date(now.getTime() - tzOffset).toISOString().slice(0, 16)
}

const toLocalDateTimeString = (isoString: string) => {
  const date = new Date(isoString)
  if (isNaN(date.getTime())) return ''
  const tzOffset = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16)
}

interface TaskModalProps {
  open: boolean
  task?: {
    title: string
    description?: string | null
    priority: string
    status: string
    dueDate?: string | null
  } | null
  entityType: 'lead' | 'deal' | 'sales'
  onClose: () => void
  onSave: (payload: {
    title: string
    description?: string
    priority: string
    status?: string
    dueDate: string
  }) => void
  isSaving: boolean
}

export function TaskModal({ open, task, entityType, onClose, onSave, isSaving }: TaskModalProps) {
  const isEdit = !!task
  const [title,    setTitle]    = useState('')
  const [desc,     setDesc]     = useState('')
  const [priority, setPriority] = useState<string>('MEDIUM')
  const [status,   setStatus]   = useState<string>(entityType === 'lead' ? 'PENDING' : 'OPEN')
  const [dueDate,  setDueDate]  = useState('')

  useEffect(() => {
    if (open) {
      setTitle(task?.title ?? '')
      setDesc(task?.description ?? '')
      setPriority(task?.priority ?? 'MEDIUM')
      setStatus(
        (task?.status === 'PENDING' && entityType === 'deal')
          ? 'OPEN'
          : (task?.status ?? (entityType === 'lead' ? 'PENDING' : 'OPEN'))
      )
      setDueDate(task?.dueDate ? toLocalDateTimeString(task.dueDate) : '')
    }
  }, [open, task, entityType])

  const minNow = getNowLocal()

  const handleSave = () => {
    if (!title.trim() || !dueDate) return
    const payload: any = {
      title: title.trim(),
      description: desc.trim() || undefined,
      priority,
      dueDate: new Date(dueDate).toISOString(),
    }
    if (isEdit) payload.status = status
    onSave(payload)
  }

  const statuses = entityType === 'lead' ? LEAD_STATUSES : DEAL_STATUSES

  const fieldLabelSx = {
    display: 'block',
    fontSize: '12.5px',
    fontWeight: 700,
    color: '#374151',
    mb: 0.75,
  } as const

  const requiredMark = (
    <Box component="span" sx={{ color: themeColors.error ?? '#dc2626' }}> *</Box>
  )

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      onSubmit={handleSave}
      title={isEdit ? 'Edit Task' : 'New Task'}
      submitLabel={isEdit ? 'Save Changes' : 'Create Task'}
      isSubmitting={isSaving}
      submitDisabled={!title.trim() || !dueDate}
      maxWidth="sm"
      icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

        {/* Task Title */}
        <Box sx={{ mb: 2.5 }}>
          <Typography component="label" sx={fieldLabelSx}>
            Task Title{requiredMark}
          </Typography>
          <FormInput
            placeholder="Enter Task Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            fullWidth
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '13.5px',
                '& fieldset': { borderColor: '#e5e7eb' },
                '&:hover fieldset': { borderColor: themeColors.primary },
                '&.Mui-focused fieldset': { borderColor: themeColors.primary },
              },
            }}
          />
        </Box>

        {/* Description */}
        <Box sx={{ mb: 2.5 }}>
          <Typography component="label" sx={fieldLabelSx}>
            Description
          </Typography>
          <FormInput
            placeholder="Enter Description"
            value={desc}
            onChange={e => setDesc(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '13.5px',
                bgcolor: '#fafafa',
                '& fieldset': { borderColor: '#e5e7eb' },
                '&:hover fieldset': { borderColor: themeColors.primary },
                '&.Mui-focused fieldset': { borderColor: themeColors.primary },
              },
            }}
          />
        </Box>

        <Divider sx={{ mb: 2.5, borderColor: '#f3f4f6' }} />

        {/* Priority (+ Status in edit mode) */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography component="label" sx={fieldLabelSx}>
              Priority{requiredMark}
            </Typography>
            <FormSelect
              value={priority}
              onChange={(v: any) => setPriority(v?.value ?? 'MEDIUM')}
              options={PRIORITIES}
              fullWidth
            />
          </Box>

          {isEdit && (
            <Box sx={{ flex: 1 }}>
              <Typography component="label" sx={fieldLabelSx}>
                Status
              </Typography>
              <FormSelect
                value={status}
                onChange={(v: any) => setStatus(v?.value ?? (entityType === 'lead' ? 'PENDING' : 'OPEN'))}
                options={statuses}
                fullWidth
              />
            </Box>
          )}
        </Box>

        {/* Due Date & Time — single datetime-local so min covers both date AND time */}
        <Box>
          <Typography component="label" sx={fieldLabelSx}>
            Due Date & Time{requiredMark}
          </Typography>
          <FormInput
            type="datetime-local"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minNow }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '13.5px',
                '& fieldset': { borderColor: '#e5e7eb' },
                '&:hover fieldset': { borderColor: themeColors.primary },
                '&.Mui-focused fieldset': { borderColor: themeColors.primary },
              },
            }}
          />
        </Box>
      </Box>
    </EntityModal>
  )
}
